/**
 * Amada collection filters UI.
 * Desktop: in-flow sticky left column (no overlay).
 * Mobile: existing bottom-sheet theme-drawer.
 * Does not replace Shopify facets / section-rendering logic.
 */
(() => {
  if (window.__amadaCollectionFiltersBound) return;
  window.__amadaCollectionFiltersBound = true;

  const DRAWER_ID = 'filters-drawer';
  const OPEN_CLASS = 'amada-filters-column-open';
  const LOADING_CLASS = 'amada-collection-filtering';
  const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');

  const isDesktop = () => DESKTOP_MQ.matches;

  /** @returns {HTMLElement | null} */
  const getDrawer = () => document.getElementById(DRAWER_ID);

  /** @returns {HTMLDialogElement | null} */
  const getPanel = () => {
    const drawer = getDrawer();
    const panel = drawer?.querySelector('dialog');
    return panel instanceof HTMLDialogElement ? panel : null;
  };

  /**
   * @param {HTMLElement} panel
   * @param {string} paramSlug
   */
  function expandAccordion(panel, paramSlug) {
    /** @type {NodeListOf<HTMLDetailsElement>} */
    const detailsList = panel.querySelectorAll('details.facets__panel');
    let target = null;

    if (paramSlug) {
      const escaped = CSS.escape(paramSlug);
      const candidates = [
        panel.querySelector(`accordion-custom[data-filter-param-name="${escaped}"] details.facets__panel`),
        panel.querySelector(`[data-filter-param-name="${escaped}"] details.facets__panel`),
        panel.querySelector(`details[id*="${escaped}"]`),
      ];

      for (const match of candidates) {
        if (match instanceof HTMLDetailsElement) {
          target = match;
          break;
        }
        if (match instanceof HTMLElement) {
          const nested = match.querySelector('details.facets__panel');
          if (nested instanceof HTMLDetailsElement) {
            target = nested;
            break;
          }
        }
      }
    }

    if (!target && paramSlug) {
      const slug = paramSlug.toLowerCase();
      const wantsPrice = slug.includes('price') || slug.includes('budget');
      detailsList.forEach((details) => {
        if (target) return;
        const label = details.querySelector('.facets__label')?.textContent?.trim().toLowerCase() || '';
        const id = (details.id || '').toLowerCase();
        if (id.includes(slug)) {
          target = details;
          return;
        }
        if (wantsPrice && (label.includes('price') || label.includes('budget'))) {
          target = details;
        }
      });
    }

    if (!target && detailsList.length) target = detailsList[0];

    detailsList.forEach((details) => {
      const open = details === target;
      details.open = open;
      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) {
        summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      const host = details.closest('accordion-custom');
      if (host instanceof HTMLElement) {
        if (open) host.setAttribute('open', '');
        else host.removeAttribute('open');
      }
    });

    if (target) {
      // Keep sticky column stable — nearest only, no page jump
      try {
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } catch (_) {
        /* ignore */
      }
    }
  }

  function openDesktopColumn(paramSlug) {
    const panel = getPanel();
    document.documentElement.classList.add(OPEN_CLASS);

    if (panel) {
      // Exit any modal/showModal state without body scroll lock
      try {
        if (panel.open) panel.close();
      } catch (_) {
        /* ignore */
      }
      panel.setAttribute('open', '');
      panel.classList.remove(
        'theme-drawer__dialog--opening',
        'theme-drawer__dialog--opening-inline-start',
        'theme-drawer__dialog--closing'
      );
    }

    const drawer = getDrawer();
    if (drawer) {
      drawer.setAttribute('open', '');
      drawer.setAttribute('data-amada-column', 'true');
    }

    const run = () => {
      const p = getPanel();
      if (p) expandAccordion(p, paramSlug || '');
    };
    requestAnimationFrame(() => {
      run();
      window.setTimeout(run, 40);
      nudgeLayoutReflow();
    });
    window.setTimeout(nudgeLayoutReflow, 300);
  }


  function nudgeLayoutReflow() {
    // Force product-grid @container queries to re-measure after column open/close
    requestAnimationFrame(() => {
      try {
        window.dispatchEvent(new Event('resize'));
      } catch (_) {
        /* ignore */
      }
      const grid =
        document.querySelector('.collection-wrapper .product-grid') ||
        document.querySelector('.main-collection-grid');
      if (grid instanceof HTMLElement) {
        // Touch layout without visible jump
        void grid.offsetWidth;
      }
    });
  }

  function closeDesktopColumn() {
    document.documentElement.classList.remove(OPEN_CLASS);
    const panel = getPanel();
    const drawer = getDrawer();

    if (panel) {
      try {
        if (panel.open) panel.close();
      } catch (_) {
        /* ignore */
      }
      panel.removeAttribute('open');
    }
    if (drawer) {
      drawer.removeAttribute('open');
      drawer.removeAttribute('data-amada-column');
    }
    nudgeLayoutReflow();
    window.setTimeout(nudgeLayoutReflow, 300);
  }

  function isColumnOpen() {
    return document.documentElement.classList.contains(OPEN_CLASS);
  }

  /**
   * @param {string | null | undefined} paramSlug
   */
  function openFilterPanel(paramSlug) {
    const slug = paramSlug || '';

    if (isDesktop()) {
      // Already open: only switch accordion — no layout re-animation
      if (isColumnOpen()) {
        const panel = getPanel();
        if (panel) expandAccordion(panel, slug);
        return;
      }
      openDesktopColumn(slug);
      return;
    }

    // Mobile: existing theme-drawer bottom sheet
    const drawer = getDrawer();
    if (!drawer || typeof /** @type {any} */ (drawer).open !== 'function') return;
    /** @type {any} */ (drawer).open();

    const panel = getPanel();
    if (!panel) return;
    const run = () => expandAccordion(panel, slug);
    requestAnimationFrame(() => {
      run();
      window.setTimeout(run, 50);
      window.setTimeout(run, 200);
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Desktop close controls (X / View results) — never use modal close path
      if (isDesktop() && isColumnOpen()) {
        const closeEl = target.closest(
          '.theme-drawer__close-button, .facets__see-results, [data-amada-column-close]'
        );
        if (closeEl && getDrawer()?.contains(closeEl)) {
          event.preventDefault();
          event.stopPropagation();
          closeDesktopColumn();
          return;
        }
      }

      const trigger = target.closest('[data-amada-filter-open]');
      if (!(trigger instanceof HTMLElement)) return;
      event.preventDefault();
      openFilterPanel(trigger.getAttribute('data-filter-param') || '');
    },
    true
  );

  // If viewport crosses to mobile while column is open, clean up and leave mobile alone
  const onBreakpoint = () => {
    if (!isDesktop() && isColumnOpen()) {
      closeDesktopColumn();
    }
  };
  if (typeof DESKTOP_MQ.addEventListener === 'function') {
    DESKTOP_MQ.addEventListener('change', onBreakpoint);
  } else {
    // Safari < 14
    DESKTOP_MQ.addListener(onBreakpoint);
  }

  // Persist column after section morph / pageshow
  const restoreColumnIfNeeded = () => {
    if (!isDesktop() || !isColumnOpen()) return;
    const panel = getPanel();
    if (panel && !panel.hasAttribute('open')) {
      panel.setAttribute('open', '');
    }
    const drawer = getDrawer();
    if (drawer) drawer.setAttribute('open', '');
  };

  document.addEventListener('shopify:section:load', () => {
    window.setTimeout(() => {
      setLoading(false);
      restoreColumnIfNeeded();
    }, 80);
  });
  window.addEventListener('pageshow', () => {
    setLoading(false);
    restoreColumnIfNeeded();
  });

  const setLoading = (on) => {
    const grid =
      document.querySelector('results-list') ||
      document.querySelector('.product-grid-container') ||
      document.querySelector('.main-collection-grid');
    if (!(grid instanceof HTMLElement)) return;
    grid.classList.toggle(LOADING_CLASS, Boolean(on));
    grid.setAttribute('aria-busy', on ? 'true' : 'false');
  };

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.closest('facets-form-component')) return;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      setLoading(true);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('facet-remove-component')) return;
      setLoading(true);
    },
    true
  );

  const observeGrid = () => {
    const grid = document.querySelector('results-list .product-grid, .product-grid, results-list');
    if (!(grid instanceof HTMLElement) || grid.dataset.amadaFilterObserved === 'true') return;
    grid.dataset.amadaFilterObserved = 'true';
    new MutationObserver(() => {
      setLoading(false);
      restoreColumnIfNeeded();
    }).observe(grid, { childList: true, subtree: true });
  };


  /**
   * Apply a visible wash from the metal swatch on hover/selected.
   * Gradients cannot be used in color-mix, so we extract a solid RGB and
   * paint backgroundColor directly.
   */
  function extractSolidColor(value) {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw || raw === 'transparent' || raw === 'rgba(0, 0, 0, 0)') return '';
    const rgb = raw.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
    if (rgb) {
      return `${Math.round(Number(rgb[1]))}, ${Math.round(Number(rgb[2]))}, ${Math.round(Number(rgb[3]))}`;
    }
    const hex = raw.match(/#([0-9a-f]{3,8})/i);
    if (hex) {
      let h = hex[1];
      if (h.length === 3) h = h.split('').map((c) => c + c).join('');
      const n = parseInt(h.slice(0, 6), 16);
      return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
    }
    return '';
  }

  function familyFallbackRgb(label) {
    const cls = label.className || '';
    if (cls.includes('metal-yellow')) return '212, 175, 55';
    if (cls.includes('metal-rose')) return '183, 110, 121';
    if (cls.includes('metal-white')) return '198, 198, 201';
    if (cls.includes('metal-platinum')) return '180, 184, 190';
    const text = (label.textContent || '').toLowerCase();
    if (text.includes('rose')) return '183, 110, 121';
    if (text.includes('platinum')) return '180, 184, 190';
    if (text.includes('white')) return '198, 198, 201';
    if (text.includes('yellow') || text.includes('gold')) return '212, 175, 55';
    return '200, 196, 188';
  }

  function paintMetalLabel(label, hovering) {
    const rgb = label.dataset.amadaMetalRgb || familyFallbackRgb(label);
    const checked = !!label.querySelector('input:checked');
    label.style.setProperty('border', '0', 'important');
    label.style.setProperty('outline', '0', 'important');
    label.style.setProperty('box-shadow', 'none', 'important');
    label.style.setProperty('background-image', 'none', 'important');
    if (hovering || checked) {
      const alpha = checked ? (hovering ? 0.28 : 0.22) : 0.16;
      label.style.setProperty('background-color', `rgba(${rgb}, ${alpha})`, 'important');
    } else {
      label.style.setProperty('background-color', 'transparent', 'important');
    }
  }

  function applyMetalHoverTints(root) {
    const scope = root instanceof Element ? root : document;
    const labels = scope.querySelectorAll(
      '#filters-drawer .facets__inputs-list--metal .variant-option__button-label--metal, #filters-drawer .facets__inputs-list--metal .variant-option__button-label--has-swatch'
    );
    labels.forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      if (label.classList.contains('is-disabled') || label.getAttribute('aria-disabled') === 'true') {
        label.style.setProperty('background-color', 'transparent', 'important');
        label.style.setProperty('background-image', 'none', 'important');
        label.style.setProperty('border', '0', 'important');
        label.style.setProperty('outline', '0', 'important');
        label.style.setProperty('box-shadow', 'none', 'important');
        return;
      }

      const swatch = label.querySelector('.swatch');
      let rgb = '';
      if (swatch instanceof HTMLElement) {
        try {
          const cs = getComputedStyle(swatch);
          rgb =
            extractSolidColor(cs.getPropertyValue('--swatch-background')) ||
            extractSolidColor(cs.backgroundColor) ||
            extractSolidColor(cs.backgroundImage);
        } catch (_) {
          /* ignore */
        }
      }
      if (!rgb) rgb = familyFallbackRgb(label);
      label.dataset.amadaMetalRgb = rgb;

      if (label.dataset.amadaMetalBound !== '1') {
        label.dataset.amadaMetalBound = '1';
        label.addEventListener('mouseenter', () => paintMetalLabel(label, true));
        label.addEventListener('mouseleave', () => paintMetalLabel(label, false));
        label.addEventListener('change', () => paintMetalLabel(label, label.matches(':hover')));
        label.addEventListener('click', () => {
          window.requestAnimationFrame(() => paintMetalLabel(label, label.matches(':hover')));
        });
      }
      paintMetalLabel(label, label.matches(':hover'));
    });
  }

  const scheduleMetalTints = () => {
    requestAnimationFrame(() => applyMetalHoverTints(document));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleMetalTints, { once: true });
  } else {
    scheduleMetalTints();
  }
  document.addEventListener('shopify:section:load', scheduleMetalTints);
  // After filter AJAX morph
  const _metalObs = new MutationObserver(() => scheduleMetalTints());
  const startMetalObs = () => {
    const drawer = getDrawer();
    if (drawer) _metalObs.observe(drawer, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startMetalObs, { once: true });
  } else {
    startMetalObs();
  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeGrid, { once: true });
  } else {
    observeGrid();
  }

  /**
   * Only one filter accordion open at a time inside #filters-drawer.
   * When a details opens, close every sibling details.facets__panel.
   */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (!details.classList.contains('facets__panel')) return;

      const drawer = details.closest('#filters-drawer');
      if (!drawer) return;

      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      }

      const host = details.closest('accordion-custom');
      if (host instanceof HTMLElement) {
        if (details.open) host.setAttribute('open', '');
        else host.removeAttribute('open');
      }

      // Exclusive open: only when this panel just opened
      if (!details.open) return;
      if (details.dataset.amadaAccordionLock === '1') return;

      const siblings = drawer.querySelectorAll('details.facets__panel');
      siblings.forEach((other) => {
        if (!(other instanceof HTMLDetailsElement) || other === details) return;
        if (!other.open) return;
        other.dataset.amadaAccordionLock = '1';
        other.open = false;
        const otherSummary = other.querySelector('summary');
        if (otherSummary instanceof HTMLElement) {
          otherSummary.setAttribute('aria-expanded', 'false');
        }
        const otherHost = other.closest('accordion-custom');
        if (otherHost instanceof HTMLElement) otherHost.removeAttribute('open');
        // clear lock after browser finishes toggle event cascade
        window.setTimeout(() => {
          delete other.dataset.amadaAccordionLock;
        }, 0);
      });
    },
    true
  );


  /**
   * Auto-select filter values implied by the collection title/handle
   * (e.g. /collections/collections-engagement-rings-round -> Shape: Round).
   * Uses native Shopify filter.* URLs. Runs once per collection handle.
   */
  function applyCollectionAutoFilter() {
    return;
    const dataEl = document.getElementById('amada-collection-auto-filter-data');
    if (!(dataEl instanceof HTMLElement)) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent || '{}');
    } catch {
      return;
    }

    const handle = typeof data.handle === 'string' ? data.handle : '';
    const matches = Array.isArray(data.matches) && data.matches.length
      ? data.matches
      : [{ param: data.param, value: data.value, alreadyActive: data.alreadyActive }];

    const url = new URL(window.location.href);
    const pending = [];
    for (const match of matches) {
      const param = typeof match?.param === 'string' ? match.param : '';
      const value = typeof match?.value === 'string' ? match.value : '';
      if (!param || !value) continue;
      if (match.alreadyActive === true) continue;
      if (url.searchParams.getAll(param).some((v) => String(v) === String(value))) continue;
      pending.push({ param, value });
    }
    if (!pending.length) return;

    const gateKey = handle || url.pathname;
    window.__amadaAutoFilterApplied = window.__amadaAutoFilterApplied || {};
    if (window.__amadaAutoFilterApplied[gateKey]) return;
    window.__amadaAutoFilterApplied[gateKey] = true;

    const next = new URL(window.location.href);
    pending.forEach(({ param, value }) => next.searchParams.append(param, value));
    next.searchParams.delete('page');

    const host =
      document.querySelector('facets-form-component[data-page-type="collection"]') ||
      document.querySelector('facets-form-component');

    if (host && typeof host.updateFiltersByURL === 'function') {
      try {
        host.updateFiltersByURL(next.toString());
        return;
      } catch (e) {}
    }

    const forms = document.querySelectorAll('facets-form-component form.facets__form');
    let applied = false;
    pending.forEach(({ param, value }) => {
      let input = null;
      forms.forEach((form) => {
        if (input || !(form instanceof HTMLFormElement)) return;
        form.querySelectorAll('input[type="checkbox"][name="' + CSS.escape(param) + '"]').forEach((el) => {
          if (input || !(el instanceof HTMLInputElement) || el.disabled) return;
          if (el.value === value) input = el;
        });
      });
      if (input instanceof HTMLInputElement && !input.checked) {
        input.checked = true;
        applied = true;
        const facetInputs = input.closest('facet-inputs-component');
        if (facetInputs && typeof facetInputs.updateFilters === 'function') {
          facetInputs.updateFilters();
        } else {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
    if (applied) return;

    window.location.replace(next.toString());
  }

  const scheduleAutoFilter = () => {
    // After first paint + facets custom elements upgrade
    requestAnimationFrame(() => {
      setTimeout(applyCollectionAutoFilter, 50);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleAutoFilter, { once: true });
  } else {
    scheduleAutoFilter();
  }
  document.addEventListener('shopify:section:load', scheduleAutoFilter);

})();
